import React, { useMemo, useState } from 'react';
import { FinalizeBucket, FinalizeCell, FinalizeKind, ScheduleSnapshotRow, fmtDuration } from './logic';

/**
 * 'Pipeline (current)' panel for the LC Metrics page — a point-in-time view of
 * each LC's open scheduling backlog (Pending LC / Hold / Pending Customer).
 * Unlike the matrix tables this is not date-bucketed: it reflects the live state
 * right now, so there are no date columns. Sortable by any count; defaults to
 * the biggest total backlog first.
 *
 * Every non-zero count in the two finalize matrices opens the orders behind it
 * (`onFinalizeCellClick`), including the per-LC and per-column Totals.
 */

type SortCol = 'name' | 'pending_lc' | 'avg_time' | 'hold' | 'pending_customer' | 'total';

interface Props {
  rows: ScheduleSnapshotRow[];
  /** Omitted (the Scheduler page embeds this panel read-only) leaves the counts
   *  as plain text rather than rendering buttons that lead nowhere. */
  onFinalizeCellClick?: (cell: FinalizeCell) => void;
}

const totalOf = (r: ScheduleSnapshotRow): number => r.pending_lc + r.hold + r.pending_customer;

// Column order mirrors the Scheduling view (Pending LC / Pending Customer / Hold),
// with the live "time on LC" clock sitting right next to Pending LC.
const COLS: { key: Exclude<SortCol, 'name'>; label: string }[] = [
  { key: 'pending_lc', label: 'Pending LC' },
  { key: 'avg_time', label: 'Avg Time' },
  { key: 'pending_customer', label: 'Pending Customer' },
  { key: 'hold', label: 'Hold' },
  { key: 'total', label: 'Total' },
];

const SchedulingSnapshot: React.FC<Props> = ({ rows, onFinalizeCellClick }) => {
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'total', dir: 'desc' });

  const onSort = (col: SortCol) => () =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: col === 'name' ? 'asc' : 'desc' }
    );

  const sorted = useMemo(() => {
    const valOf = (r: ScheduleSnapshotRow): number | string =>
      sort.col === 'name' ? r.dispatcher
        : sort.col === 'total' ? totalOf(r)
        : sort.col === 'avg_time' ? (r.avg_pending_secs ?? -1)
        : r[sort.col];
    return [...rows].sort((a, b) => {
      const va = valOf(a);
      const vb = valOf(b);
      const cmp = typeof va === 'string' || typeof vb === 'string'
        ? String(va).localeCompare(String(vb))
        : (va as number) - (vb as number);
      const signed = sort.dir === 'asc' ? cmp : -cmp;
      return signed !== 0 ? signed : a.dispatcher.localeCompare(b.dispatcher);
    });
  }, [rows, sort]);

  const totals = useMemo(() => rows.reduce(
    (acc, r) => {
      acc.pending_lc += r.pending_lc;
      acc.hold += r.hold;
      acc.pending_customer += r.pending_customer;
      acc.total += totalOf(r);
      return acc;
    },
    { pending_lc: 0, hold: 0, pending_customer: 0, total: 0 },
  ), [rows]);

  // Overall avg time = pending-weighted mean of each LC's avg (avg_i × count_i is
  // that LC's summed seconds), so the footer matches a single pooled average.
  const overallAvgSecs = useMemo(() => {
    let sum = 0;
    let n = 0;
    for (const r of rows) {
      if (r.pending_lc > 0 && r.avg_pending_secs != null) {
        sum += r.avg_pending_secs * r.pending_lc;
        n += r.pending_lc;
      }
    }
    return n > 0 ? sum / n : null;
  }, [rows]);

  const arrow = (col: SortCol) => (sort.col === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
        No open scheduling items.
      </div>
    );
  }

  const numCell = (n: number) =>
    n > 0
      ? <span className="font-semibold text-body">{n.toLocaleString('en-US')}</span>
      : <span className="text-gray-400 dark:text-gray-600">0</span>;

  // Avg time on LC for the row's Pending LC orders — muted em dash when the LC has
  // no pending orders (no clock to average).
  const avgCell = (secs: number | null | undefined, pending: number) =>
    pending > 0 && secs != null
      ? <span className="text-body">{fmtDuration(secs)}</span>
      : <span className="text-gray-400 dark:text-gray-600">—</span>;

  // Finalize backlog: two per-LC tables (Prepulls / Deliveries not finalized),
  // rows = LCs, columns = due-date buckets. Past Due only appears when something is
  // overdue; Today/+1/+2 carry the actual dates; Future absorbs the no-date items.
  type BKey = 'pastdue' | 'today' | 'plus1' | 'plus2' | 'future';
  type Bkt = Record<BKey, number>;
  const BKEYS: BKey[] = ['pastdue', 'today', 'plus1', 'plus2', 'future'];
  const btot = (b: Bkt) => BKEYS.reduce((s, k) => s + b[k], 0);
  const pickPrepull = (r: ScheduleSnapshotRow): Bkt => ({
    pastdue: r.prepull_pastdue ?? 0, today: r.prepull_today ?? 0, plus1: r.prepull_plus1 ?? 0,
    plus2: r.prepull_plus2 ?? 0, future: r.prepull_future ?? 0,
  });
  const pickDeliv = (r: ScheduleSnapshotRow): Bkt => ({
    pastdue: r.deliv_pastdue ?? 0, today: r.deliv_today ?? 0, plus1: r.deliv_plus1 ?? 0,
    plus2: r.deliv_plus2 ?? 0, future: r.deliv_future ?? 0,
  });
  const showPastDue = rows.some((r) => (r.prepull_pastdue ?? 0) + (r.deliv_pastdue ?? 0) > 0);
  const mdLabel = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };
  const matrixCols: { key: BKey; top: string; sub?: string }[] = [
    ...(showPastDue ? [{ key: 'pastdue' as BKey, top: 'Past Due' }] : []),
    { key: 'today', top: 'Today', sub: mdLabel(0) },
    { key: 'plus1', top: '+1', sub: mdLabel(1) },
    { key: 'plus2', top: '+2', sub: mdLabel(2) },
    { key: 'future', top: 'Future' },
  ];
  // The column heading a drilled cell came from, for the modal's title — 'Today
  // 10/2' reads as the cell the user clicked, where a bare 'today' does not.
  const colLabel = (key: BKey): string => {
    const c = matrixCols.find((m) => m.key === key);
    return c ? [c.top, c.sub].filter(Boolean).join(' ') : key;
  };

  /** A count in a finalize matrix. Non-zero counts drill; a zero has nothing to
   *  show, so it stays the muted text it already was rather than a dead button. */
  const countCell = (n: number, cell: Omit<FinalizeCell, 'count'>) => {
    const label = n.toLocaleString('en-US');
    if (n <= 0) return <span className="text-gray-400 dark:text-gray-600">0</span>;
    if (!onFinalizeCellClick) return <span className="font-semibold text-body">{label}</span>;
    return (
      <button
        type="button"
        onClick={() => onFinalizeCellClick({ ...cell, count: n })}
        className="font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        title="Show these orders"
      >
        {label}
      </button>
    );
  };

  /** Same, for the white-on-slate footer row, where the blue link would vanish. */
  const totalCountCell = (n: number, cell: Omit<FinalizeCell, 'count'>) => {
    const label = n.toLocaleString('en-US');
    if (n <= 0 || !onFinalizeCellClick) return <>{label}</>;
    return (
      <button
        type="button"
        onClick={() => onFinalizeCellClick({ ...cell, count: n })}
        className="hover:underline"
        title="Show these orders"
      >
        {label}
      </button>
    );
  };

  // One per-LC bucket table (title sits in the blue header corner). LCs with an
  // all-zero row are dropped; a Total row sums the rest. Biggest backlog first.
  const bucketTable = (title: string, kind: FinalizeKind, pick: (r: ScheduleSnapshotRow) => Bkt) => {
    const lcRows = rows
      .map((r) => ({ name: r.dispatcher, key: r.dispatcher_key, b: pick(r) }))
      .filter((x) => btot(x.b) > 0)
      .sort((a, b) => btot(b.b) - btot(a.b) || a.name.localeCompare(b.name));
    const tot: Bkt = { pastdue: 0, today: 0, plus1: 0, plus2: 0, future: 0 };
    for (const x of lcRows) for (const k of BKEYS) tot[k] += x.b[k];
    return (
      <div className="overflow-auto">
        <table className="text-xs border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-slate-700 dark:bg-gray-800 text-white">
              <th className="sticky left-0 z-10 bg-slate-700 dark:bg-gray-800 text-left px-2 py-1 min-w-[170px] capitalize">{title}</th>
              {matrixCols.map((c) => (
                <th key={c.key} className="px-3 py-1 text-center min-w-[70px]">
                  <div className="leading-tight">{c.top}</div>
                  {c.sub && <div className="text-[0.625rem] opacity-70">{c.sub}</div>}
                </th>
              ))}
              <th className="px-3 py-1 text-center min-w-[64px] border-l-2 border-gray-500">Total</th>
            </tr>
          </thead>
          <tbody>
            {lcRows.length === 0 ? (
              <tr>
                <td colSpan={matrixCols.length + 2} className="px-2 py-2 text-center text-gray-400 dark:text-gray-600">None</td>
              </tr>
            ) : (
              <>
                {lcRows.map((x) => (
                  <tr key={x.key} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-900 dark:even:bg-gray-800/40">
                    <td className="sticky left-0 z-[1] bg-inherit px-2 py-1 border-b border-gray-200 dark:border-gray-700 text-body">{x.name}</td>
                    {matrixCols.map((c) => (
                      <td key={c.key} className="px-3 py-1 text-center border-b border-gray-200 dark:border-gray-700">
                        {countCell(x.b[c.key], { kind, bucket: c.key, lc: x.key, lcName: x.name, label: colLabel(c.key) })}
                      </td>
                    ))}
                    <td className="px-3 py-1 text-center border-b border-l-2 border-gray-200 dark:border-gray-700 border-l-gray-300 dark:border-l-gray-600 font-semibold text-body">
                      {countCell(btot(x.b), { kind, bucket: null, lc: x.key, lcName: x.name, label: 'Total' })}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-700 dark:bg-gray-800 text-white font-semibold">
                  <td className="sticky left-0 z-[1] bg-slate-700 dark:bg-gray-800 px-2 py-1">Total</td>
                  {matrixCols.map((c) => (
                    <td key={c.key} className="px-3 py-1 text-center">
                      {totalCountCell(tot[c.key], { kind, bucket: c.key, lc: null, lcName: null, label: colLabel(c.key) })}
                    </td>
                  ))}
                  <td className="px-3 py-1 text-center border-l-2 border-gray-500">
                    {totalCountCell(btot(tot), { kind, bucket: null, lc: null, lcName: null, label: 'Total' })}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="text-xs text-muted mb-2 flex-shrink-0">
        Live backlog as of now — point-in-time, so the date range doesn’t apply here.
        Matches the Scheduler board: available orders still awaiting a delivery appointment.
        Avg Time is a running clock — how long each Pending LC order has been on the LC (now − send-to-LC).
      </div>

      {/* Scrollable region: per-LC table (left) beside the finalize matrix (right);
          the matrix wraps below when the row runs out of width. `order` keeps the
          per-LC table first visually while the matrix stays first in the DOM. */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="flex flex-wrap gap-6 items-start">

      {/* Finalize backlog — two per-LC tables (prepulls / deliveries not finalized)
          bucketed by due date, stacked in the right column; wraps below on narrow. */}
      <div className="order-2 flex flex-col gap-4">
        {bucketTable('Prepulls not finalized', 'prepull', pickPrepull)}
        {bucketTable('Deliveries not finalized', 'delivery', pickDeliv)}
      </div>

      <div className="order-1 overflow-auto">
        <table className="text-xs border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-slate-700 dark:bg-gray-800 text-white">
              <th
                onClick={onSort('name')}
                className="sticky left-0 z-10 bg-slate-700 dark:bg-gray-800 text-left px-2 py-1 cursor-pointer select-none min-w-[160px]"
              >
                LC{arrow('name')}
              </th>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={onSort(c.key)}
                  className={`px-3 py-1 text-center cursor-pointer select-none min-w-[90px] ${c.key === 'total' ? 'border-l-2 border-gray-500' : ''}`}
                >
                  {c.label}{arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.dispatcher_key} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-900 dark:even:bg-gray-800/40">
                <td className="sticky left-0 z-[1] bg-inherit px-2 py-1 border-b border-gray-200 dark:border-gray-700 text-body">
                  {r.dispatcher}
                </td>
                <td className="px-3 py-1 text-center border-b border-gray-200 dark:border-gray-700">{numCell(r.pending_lc)}</td>
                <td className="px-3 py-1 text-center border-b border-gray-200 dark:border-gray-700">{avgCell(r.avg_pending_secs, r.pending_lc)}</td>
                <td className="px-3 py-1 text-center border-b border-gray-200 dark:border-gray-700">{numCell(r.pending_customer)}</td>
                <td className="px-3 py-1 text-center border-b border-gray-200 dark:border-gray-700">{numCell(r.hold)}</td>
                <td className="px-3 py-1 text-center border-b border-l-2 border-gray-200 dark:border-gray-700 border-l-gray-300 dark:border-l-gray-600 font-semibold text-body">
                  {totalOf(r).toLocaleString('en-US')}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-700 dark:bg-gray-800 text-white font-semibold">
              <td className="sticky left-0 z-[1] bg-slate-700 dark:bg-gray-800 px-2 py-1">Total</td>
              <td className="px-3 py-1 text-center">{totals.pending_lc.toLocaleString('en-US')}</td>
              <td className="px-3 py-1 text-center">{overallAvgSecs != null ? fmtDuration(overallAvgSecs) : '—'}</td>
              <td className="px-3 py-1 text-center">{totals.pending_customer.toLocaleString('en-US')}</td>
              <td className="px-3 py-1 text-center">{totals.hold.toLocaleString('en-US')}</td>
              <td className="px-3 py-1 text-center border-l-2 border-gray-500">{totals.total.toLocaleString('en-US')}</td>
            </tr>
          </tbody>
        </table>
      </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingSnapshot;
